import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, type Cycle } from '../db/database'
import { cancelRestart, completeOnboarding, isRedoingOnboarding } from '../db/onboarding'
import { formatAriary, parseAmountBounded } from '../lib/money'
import { messageMontant } from '../lib/limites'
import { navigate } from '../lib/router'
import './OnboardingSheet.css'

/**
 * Le tout premier écran.
 *
 * Deux questions, pas une de plus, puis on envoie l'utilisateur répartir. Sur
 * un public peu habitué aux applications, chaque champ demandé avant le montant
 * est une occasion d'abandonner.
 *
 * La question porte sur l'argent DISPONIBLE MAINTENANT, pas sur « ton salaire ».
 * C'est délibéré : ce montant devient une vraie écriture, et « En poche » est le
 * seul chiffre de l'app que l'utilisateur peut vérifier en comptant ses billets.
 * Le même montant est mémorisé comme estimation et lui sera REPROPOSÉ au cycle
 * suivant — mais reproposé, jamais écrit d'office.
 */
export function OnboardingSheet({ walletId }: { walletId: string }) {
  // Le rythme en place, et non « mensuel » en dur : quelqu'un qui refait son
  // réglage à la semaine ne doit pas retrouver « chaque mois » présélectionné,
  // et le valider sans le lire.
  const context = useLiveQuery(async () => ({
    redo: await isRedoingOnboarding(walletId),
    cycle: (await db.wallets.get(walletId))?.cycle ?? 'monthly',
  }), [walletId])

  const [choice, setChoice] = useState<Cycle | null>(null)
  const [raw, setRaw] = useState('')
  const [saving, setSaving] = useState(false)

  // Tant que la base n'a pas répondu, on n'affiche rien : montrer « chaque
  // mois » puis le remplacer sous les yeux de l'utilisateur est pire que
  // d'attendre un instant.
  if (!context) return null
  const cycle = choice ?? context.cycle

  const amount = parseAmountBounded(raw) ?? 0
  // Commencer à vide est permis — c'est taper n'importe quoi qui ne l'est pas.
  const problemeMontant = messageMontant(raw)
  const periode = cycle === 'monthly' ? 'ce mois' : 'cette semaine'
  const chaque = cycle === 'monthly' ? 'chaque mois' : 'chaque semaine'

  async function start() {
    setSaving(true)
    await completeOnboarding({
      walletId, cycle, amount, expected: amount,
      // Répartir est l'étape suivante, et elle n'a de sens qu'avec de
      // l'argent posé. On y emmène l'utilisateur plutôt que de le laisser sur
      // un accueil qu'il ne saurait pas quoi faire — dès que le rythme est
      // posé, pas après l'écriture (plus lente) du revenu d'ouverture.
      onCycleApplied: () => navigate(amount > 0 ? '/budget' : '/'),
    })
  }

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true"
         aria-label="Premiers réglages">
      <div className="sheet onboarding">
        {/* Sortir sans rien changer — mais seulement sur une reprise : au tout
            premier lancement il n'existe aucun état où revenir, et une croix y
            mènerait à une app vide sans rythme ni montant. Rien n'ayant encore
            été touché, refermer laisse les données intactes. */}
        {context.redo && (
          <button type="button" className="onboarding-cancel"
                  onClick={() => void cancelRestart(walletId)}>
            Annuler
          </button>
        )}

        <header className="onboarding-head">
          <h2>
            {context.redo === 'cycle' ? 'Reprenons depuis le début'
              : context.redo === 'settings' ? 'Reprenons le réglage'
              : 'Bienvenue'}
          </h2>
          {context.redo === 'cycle' ? (
            <p>
              {/* Au futur, et non au passé : rien n'est encore effacé. Annoncer
                  un effacement déjà fait rendrait « Annuler » incompréhensible. */}
              En répondant, la période en cours sera <strong>remise à neuf</strong>.
              Tes cycles terminés et tes catégories ne bougeront pas.
            </p>
          ) : context.redo === 'settings' ? (
            <p>
              On refait le cadre : <strong>tes opérations et tes enveloppes
              restent en place</strong>, seul l'argent d'ouverture sera remplacé.
            </p>
          ) : (
            <p>
              Ariariko t'aide à décider où va ton argent <strong>avant</strong> de
              le dépenser.
            </p>
          )}
        </header>

        <fieldset className="onboarding-group">
          <legend>À quel rythme reçois-tu ton argent ?</legend>
          <div className="onboarding-choices">
            <button type="button" aria-pressed={cycle === 'monthly'}
                    onClick={() => setChoice('monthly')}>
              <strong>Chaque mois</strong>
              <small>Salaire, pension, loyer reçu…</small>
            </button>
            <button type="button" aria-pressed={cycle === 'weekly'}
                    onClick={() => setChoice('weekly')}>
              <strong>Chaque semaine</strong>
              <small>Commerce, journée, petits travaux…</small>
            </button>
          </div>
        </fieldset>

        <fieldset className="onboarding-group">
          <legend>Combien as-tu à gérer pour {periode} ?</legend>
          <div className="onboarding-amount">
            <input
              type="text" inputMode="numeric" className="tabular" autoFocus
              aria-label={`Argent disponible pour ${periode}`}
              value={raw} placeholder="0"
              onChange={(event) => setRaw(event.target.value)}
            />
            <span>Ar</span>
          </div>
          <p className="onboarding-help">
            Compte l'argent que tu as <strong>maintenant</strong>. Si tes revenus
            ne sont pas fixes, mets ton estimation : on te la reproposera{' '}
            {chaque}, et tu la corrigeras à ce moment-là.
          </p>
          {problemeMontant
            ? <p className="onboarding-erreur" role="alert">{problemeMontant}</p>
            : amount > 0 && (
              <p className="onboarding-echo tabular">{formatAriary(amount)}</p>
            )}
        </fieldset>

        {/* Bloqué tant que le montant ne va pas : c'est le tout premier écran
            de l'app, et démarrer sur un chiffre faux fausse tout le cycle. */}
        <button type="button" className="onboarding-go"
                disabled={saving || problemeMontant !== null}
                onClick={() => void start()}>
          {amount > 0 ? 'Répartir mon argent' : 'Commencer'}
        </button>
      </div>
    </div>
  )
}
